import { MEMBER_TYPES } from '@hfc/common/src/types/MemberTypes';
import { UUID } from '@hfc/common/src/types/UUID';
import { v4 as uuid } from 'uuid';
import { AppTypeDentalOrVision, DentalAndVisionApplication } from '@/application/shared/domain/DentalAndVisionApplication';
import { ADDRESS_TYPE } from '@hfc/common/src/types/AddressTypes';
import { MEDICARE_STATUSES } from '@hfc/common/src/types/MedicareStatuses';
import { Address, dateHelpers, Ploc, ServicesApiResponse, SfApiResponse } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { DentalAndVisionApplicant } from '@/application/shared/domain/DentalAndVisionApplicant';
import { AddressValidationUseCase } from '@/application/shared/application/AddressValidationUseCase';
import { SubmitApplicationUseCase } from '@/application/shared/application/SubmitApplicationUseCase';
import { UpdateApplicationUseCase } from '@/application/shared/application/UpdateApplicationUseCase';
import { GetApplicationUseCase } from '@/application/shared/application/GetApplicationUseCase';
import { EnrollmentApi } from '@/application/shared/infrastructure/dataSource/EnrollmentApi';
import { AddressValidationResponse } from '@/application/shared/domain/AddressValidationResponse';
import { QuoteInterface } from '@/quote/domain/QuoteInterface';
import { PlanResponse } from '@/planList/domain/PlanResponse';

const enrollmentRepository = new EnrollmentApi();
const addressValidation = new AddressValidationUseCase(enrollmentRepository);
const submitApplication = new SubmitApplicationUseCase(enrollmentRepository);
const updateApplication = new UpdateApplicationUseCase(enrollmentRepository);
const getApplication = new GetApplicationUseCase(enrollmentRepository);

const { isAdult } = dateHelpers();

export interface DentalAndVisionApplicationStoreInterface {
  dental: DentalAndVisionApplication,
  vision: DentalAndVisionApplication,
  forceSelectedAddresses: Partial<Record<APPLICATION_TYPES, Partial<Record<ADDRESS_TYPE, boolean>>>>,
  loadingApplication: boolean,
  loading: boolean,
  newPlanSelected: boolean,
  showReselectPlanModal: boolean
}

const initialState: DentalAndVisionApplicationStoreInterface = {
  [APPLICATION_TYPES.DENTAL]: {
    uuid: uuid(),
    plan: {} as PlanResponse,
    applicants: [],
    type: APPLICATION_TYPES.DENTAL,
    agree_terms_and_conditions: false,
    agree_consent: false,
    signature: '',
  },
  [APPLICATION_TYPES.VISION]: {
    uuid: uuid(),
    plan: {} as PlanResponse,
    applicants: [],
    type: APPLICATION_TYPES.VISION,
    agree_terms_and_conditions: false,
    agree_consent: false,
    signature: ''
  },
  forceSelectedAddresses: {},
  loadingApplication: false,
  loading: false,
  newPlanSelected: true,
  showReselectPlanModal: false
};

export class DentalAndVisionApplicationStore extends Ploc<DentalAndVisionApplicationStoreInterface> {

  public constructor () {
    super(initialState);
  }

  public async updateApplication (value:Partial<DentalAndVisionApplication>, applicationType: AppTypeDentalOrVision): Promise<void> {
    this.changeState({
      ...this.state,
      [applicationType]: {
        ...this.state[applicationType],
        ...value
      }
    });
  }

  public async updateApplicant (value:Partial<DentalAndVisionApplicant>, applicationType: AppTypeDentalOrVision, index: number): Promise<void> {
    const applicants = this.state[applicationType].applicants;
    applicants[index] = {
      ...applicants[index],
      ...value
    };

    this.updateApplication({ applicants }, applicationType);
  }

  public async validateAddress (address: Address): Promise<ServicesApiResponse<AddressValidationResponse>> {
    return await addressValidation.run(address);
  }

  public generateCustomAddress (address?: Address): Address {
    const defaultAddress = {
      type: ADDRESS_TYPE.HOME,
      line_one: '',
      line_two: '',
      city: '',
      state: '',
      zip_code: 0,
      county: '',
    } as Address;

    return { ...defaultAddress, ...address ?? {} };
  }

  public async initAppFromLocalStorage (applicationType: AppTypeDentalOrVision): Promise<void> {
    try {
      const application = JSON.parse(localStorage.getItem(`${applicationType}Application`) || '{}') as DentalAndVisionApplication;
      await this.updateApplication(application, applicationType);
    } catch (_error) {
      return;
    }
  }

  public async initAppFromQuote (quote: QuoteInterface, plan: PlanResponse, applicationType: AppTypeDentalOrVision): Promise<void> {
    let applicants = quote.applicants.map((applicant, i) => {
      return {
        gender: applicant.gender,
        type: applicant.type,
        is_address_same_as_main: true,
        is_mailing_same_as_main: true,
        is_english_first_language: applicant.type === MEMBER_TYPES.APPLICANT ? true : undefined,
        main_index: i,
        date_of_birth: applicant.date_of_birth as number,
        first_name: '',
        last_name: '',
        applying: true,
        main_address: {
          zip_code: applicant.type === MEMBER_TYPES.APPLICANT ? quote.zip_code : undefined,
          county: applicant.type === MEMBER_TYPES.APPLICANT ? quote.county : undefined,
          line_two: '',
          type: ADDRESS_TYPE.HOME
        } as Address
      };
    }) as Array<DentalAndVisionApplicant>;

    const childOnlyApplication = this.isChildOnlyApplication({ applicants } as DentalAndVisionApplication);
    const hasGuardian = applicants.find((applicant) => applicant.type === MEMBER_TYPES.GUARDIAN && !isAdult(applicant.date_of_birth));

    if (childOnlyApplication) {
      applicants[0].eligible_medicare = MEDICARE_STATUSES.NOT_ELIGIBLE;
    }

    if (childOnlyApplication && !hasGuardian) {
      applicants = [...applicants, {
        main_index: applicants.length,
        gender: '',
        type: MEMBER_TYPES.GUARDIAN,
        applying: false,
        first_name: '',
        last_name: '',
        date_of_birth: 0,
        is_address_same_as_main: true,
        is_mailing_same_as_main: true,
        is_english_first_language: true,
        main_address: this.generateCustomAddress(),
      }];
    }

    await this.updateApplication({ applicants, plan, effective_date: quote.effective_date }, applicationType);
  }

  public async initAppFromDatabase (application: DentalAndVisionApplication, applicationType: AppTypeDentalOrVision, confirmationNumber?: string): Promise<void> {
    application.applicants = application.applicants.map((applicant: DentalAndVisionApplicant, i: number) => {
      const phone = applicant.phone ? {
        ...applicant.phone,
        number: parseInt(applicant.phone.number.toString())
      } : undefined;

      return {
        ...applicant,
        phone: phone,
        main_index: i
      };
    });

    if (confirmationNumber) {
      application.plan.confirmationNumber = confirmationNumber;
    }

    await this.updateApplication(application, applicationType);
  }

  public isChildOnlyApplication (application: DentalAndVisionApplication): boolean {
    const filteredApplicants = application.applicants.filter((applicant) => applicant.type !== MEMBER_TYPES.GUARDIAN);
    const isChildOnly = filteredApplicants.length === 1 && !isAdult(filteredApplicants[0]?.date_of_birth);

    return isChildOnly;
  }

  public async saveUpdatedApplication (applicationType: AppTypeDentalOrVision, token: string, appId: string): Promise<SfApiResponse> {
    return await updateApplication.run({ applicationType, application: this.state[applicationType], id: appId, token });
  }

  public async saveApplicationOnLocalStorage (application: DentalAndVisionApplication, applicationType: AppTypeDentalOrVision): Promise<void> {
    localStorage.setItem(`${applicationType}Application`, JSON.stringify(application));
  }

  public async submitApplication (applicationType: AppTypeDentalOrVision): Promise<SfApiResponse<string>> {
    return await submitApplication.run([applicationType, this.state[applicationType]]);
  }

  public async getApplication (applicationType: AppTypeDentalOrVision, uuid: UUID): Promise<SfApiResponse> {
    this.changeState({ ...this.state, loadingApplication: true });
    const response = await getApplication.run([applicationType, uuid]);

    if (response.success && response.data) {
      const application = response.data;
      application.uuid = uuid;
      this.initAppFromDatabase(application, applicationType);
    }
    this.changeState({ ...this.state, loadingApplication: false });

    return response;
  }

  public async setForceSelectedAddress (
    applicationType: AppTypeDentalOrVision,
    addressType: ADDRESS_TYPE,
    value: boolean
  ): Promise<void> {
    const newState = {
      ...this.state,
      forceSelectedAddresses: {
        ...this.state.forceSelectedAddresses,
        [applicationType]: {
          ...this.state.forceSelectedAddresses[applicationType],
          [addressType]: value
        }
      }
    };

    this.changeState(newState);
  }

  public async resetLocalStorage (applicationType: AppTypeDentalOrVision): Promise<void> {
    this.changeState({ ...this.state, [applicationType]: initialState[applicationType] });
    this.resetLocalStorageOnly(applicationType);
  }

  public async resetLocalStorageOnly (applicationType: AppTypeDentalOrVision): Promise<void> {
    localStorage.removeItem(`${applicationType}Application`);
  }

  public async setNewPlanSelected (value: boolean): Promise<void> {
    this.changeState({ ...this.state, newPlanSelected: value });
  }

  public async setShowReselectPlanModal (value: boolean): Promise<void> {
    this.changeState({ ...this.state, showReselectPlanModal: value });
  }

}
